import { useState } from 'react';
import { colors, mono, sans, thStyle, tdStyle, statusStyle } from '../theme';
import { StatCard } from './StatCard';

// Shape written by packages/backtest/src/index.ts to
// packages/backtest/output/backtest-<timestamp>.json (gitignored, generated
// locally via `pnpm --filter backtest start [days]`). The backtest package
// has no Cloudflare bindings and never talks to the API worker, so there is
// no live endpoint to fetch this from -- the user uploads the JSON file
// directly instead.
interface BacktestSignal {
  type: 'LONG' | 'SHORT';
  patterns: string[];
  score: number;
  rvol: number;
  takerBuyRatio: number;
  entryZoneStart: number;
  entryZoneEnd: number;
  stopLoss: number;
  tp1: number;
  tp2: number;
  timeframe: '5m' | '15m';
  candleOpenTime: number;
  createdAt: string;
  status: string;
  closedAt?: string;
  closedPrice?: number;
}

interface BucketStats {
  total: number;
  wins: number;
  losses: number;
  winRate: number | null;
}

interface WinRateReport {
  overall: BucketStats;
  perPattern: Record<string, BucketStats>;
  perHourUTC: Record<string, BucketStats>;
}

interface MonthlyStats {
  trades: number;
  wins: number;
  losses: number;
  netR: number;
}

interface PerformanceReport {
  finalCumulativeR: number;
  monthly: Record<string, MonthlyStats>;
}

interface BacktestReport {
  params: { days: number; generatedAt: string };
  summary: { actionableSignals: number; watchlistEntries: number; stillOpen: number };
  winRate: WinRateReport;
  performance: PerformanceReport;
  signals: BacktestSignal[];
}

interface EquityPoint {
  closedAt: string;
  r: number;
  cumulativeR: number;
}

// Same R-multiple model as packages/backtest/src/report.ts /
// buildPerformanceReport -- recomputed client-side per point (rather than
// just reading finalCumulativeR) so the equity curve can be drawn.
function buildEquityCurve(signals: BacktestSignal[]): EquityPoint[] {
  const closed = signals
    .filter((s) => s.status !== 'watchlist' && s.status !== 'active' && s.closedPrice !== undefined && s.closedAt)
    .slice()
    .sort((a, b) => new Date(a.closedAt as string).getTime() - new Date(b.closedAt as string).getTime());

  let cumulativeR = 0;
  const points: EquityPoint[] = [];
  for (const s of closed) {
    const entryRef = s.type === 'LONG' ? s.entryZoneStart : s.entryZoneEnd;
    const risk = s.type === 'LONG' ? entryRef - s.stopLoss : s.stopLoss - entryRef;
    if (!risk || risk <= 0) continue;
    const move = s.type === 'LONG' ? (s.closedPrice as number) - entryRef : entryRef - (s.closedPrice as number);
    const r = Number((move / risk).toFixed(3));
    cumulativeR = Number((cumulativeR + r).toFixed(3));
    points.push({ closedAt: s.closedAt as string, r, cumulativeR });
  }
  return points;
}

function EquityCurveChart({ points }: { points: EquityPoint[] }) {
  const width = 760;
  const height = 220;
  const paddingX = 12;
  const paddingY = 16;

  if (points.length === 0) {
    return (
      <div style={{ padding: '24px', textAlign: 'center', color: colors.textFaint, fontFamily: sans, fontSize: '13px' }}>
        Belum ada trade yang closed untuk digambar sebagai equity curve.
      </div>
    );
  }

  const values = points.map((p) => p.cumulativeR);
  const minR = Math.min(0, ...values);
  const maxR = Math.max(0, ...values);
  const range = maxR - minR || 1;

  const stepX = points.length > 1 ? (width - paddingX * 2) / (points.length - 1) : 0;
  const toY = (r: number) => height - paddingY - ((r - minR) / range) * (height - paddingY * 2);
  const zeroY = toY(0);

  const pathD = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${paddingX + i * stepX} ${toY(p.cumulativeR)}`)
    .join(' ');

  const last = points[points.length - 1];
  const lineColor = last.cumulativeR >= 0 ? colors.long : colors.short;

  return (
    <svg viewBox={`0 0 ${width} ${height}`} style={{ width: '100%', height: 'auto', display: 'block' }}>
      <line x1={paddingX} y1={zeroY} x2={width - paddingX} y2={zeroY} stroke={colors.border} strokeWidth={1} strokeDasharray="4 4" />
      <path d={pathD} fill="none" stroke={lineColor} strokeWidth={2} />
      <text x={paddingX} y={14} fill={colors.textFaint} fontSize="11" fontFamily={sans}>
        {maxR.toFixed(1)}R
      </text>
      <text x={paddingX} y={height - 4} fill={colors.textFaint} fontSize="11" fontFamily={sans}>
        {minR.toFixed(1)}R
      </text>
    </svg>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h2 style={{ fontSize: '15px', color: colors.textDim, textTransform: 'uppercase', letterSpacing: '0.05em', margin: '0 0 12px', fontWeight: 600, fontFamily: sans }}>
      {children}
    </h2>
  );
}

function BucketTable({ rows, labelHeader }: { rows: [string, BucketStats][]; labelHeader: string }) {
  return (
    <div style={{ background: colors.panel, border: `1px solid ${colors.border}`, borderRadius: '10px', overflow: 'hidden' }}>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: mono, fontSize: '13px' }}>
          <thead>
            <tr style={{ borderBottom: `1px solid ${colors.border}` }}>
              <th style={thStyle}>{labelHeader}</th>
              <th style={thStyle}>Total</th>
              <th style={thStyle}>Win</th>
              <th style={thStyle}>Loss</th>
              <th style={thStyle}>Win Rate</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={5} style={{ padding: '24px', textAlign: 'center', color: colors.textFaint, fontFamily: sans }}>Tidak ada data.</td></tr>
            )}
            {rows.map(([label, s]) => (
              <tr key={label} style={{ borderBottom: `1px solid ${colors.border}` }}>
                <td style={tdStyle}>{label}</td>
                <td style={tdStyle}>{s.total}</td>
                <td style={{ ...tdStyle, color: colors.long }}>{s.wins}</td>
                <td style={{ ...tdStyle, color: colors.short }}>{s.losses}</td>
                <td style={tdStyle}>{s.winRate !== null ? `${s.winRate}%` : 'n/a'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function BacktestView() {
  const [report, setReport] = useState<BacktestReport | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleFile = (file: File) => {
    setError(null);
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = JSON.parse(reader.result as string);
        if (!parsed?.winRate?.overall || !parsed?.performance || !Array.isArray(parsed?.signals)) {
          setError('File JSON tidak sesuai format laporan backtest (field winRate/performance/signals tidak ditemukan).');
          return;
        }
        setReport(parsed as BacktestReport);
        setFileName(file.name);
      } catch {
        setError('Gagal parse file: bukan JSON yang valid.');
      }
    };
    reader.onerror = () => setError('Gagal membaca file.');
    reader.readAsText(file);
  };

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
    e.target.value = '';
  };

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  if (!report) {
    return (
      <div>
        <div
          onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={onDrop}
          style={{
            border: `2px dashed ${isDragging ? colors.accent : colors.border}`,
            borderRadius: '10px',
            padding: '48px 24px',
            textAlign: 'center',
            background: colors.panel,
          }}
        >
          <div style={{ color: colors.text, fontSize: '15px', marginBottom: '8px', fontFamily: sans }}>
            Upload laporan backtest (JSON)
          </div>
          <div style={{ color: colors.textDim, fontSize: '13px', marginBottom: '20px', fontFamily: sans, lineHeight: 1.6 }}>
            Jalankan{' '}
            <code style={{ background: colors.panelAlt, padding: '2px 6px', borderRadius: '4px', fontFamily: mono }}>
              pnpm --filter backtest start [days]
            </code>{' '}
            di root repo, lalu upload file{' '}
            <code style={{ background: colors.panelAlt, padding: '2px 6px', borderRadius: '4px', fontFamily: mono }}>
              packages/backtest/output/backtest-*.json
            </code>{' '}
            yang dihasilkan.
          </div>
          <label style={{
            display: 'inline-block', padding: '10px 20px', background: colors.accent, color: '#0b0f17',
            borderRadius: '6px', fontWeight: 600, fontSize: '14px', cursor: 'pointer', fontFamily: sans,
          }}>
            Pilih file
            <input type="file" accept="application/json" onChange={onInputChange} style={{ display: 'none' }} />
          </label>
          {error && (
            <div style={{ marginTop: '16px', color: colors.short, background: colors.dangerBg, padding: '10px 14px', borderRadius: '6px', fontSize: '13px', fontFamily: sans }}>
              {error}
            </div>
          )}
        </div>
      </div>
    );
  }

  const equityPoints = buildEquityCurve(report.signals);
  const patternRows = Object.entries(report.winRate.perPattern).sort((a, b) => b[1].total - a[1].total);
  const hourRows: [string, BucketStats][] = Object.entries(report.winRate.perHourUTC)
    .sort((a, b) => Number(a[0]) - Number(b[0]))
    .map(([h, s]): [string, BucketStats] => [`${h.padStart(2, '0')}:00`, s]);
  const monthRows = Object.entries(report.performance.monthly).sort((a, b) => a[0].localeCompare(b[0]));
  const actionableSignals = report.signals.filter((s) => s.status !== 'watchlist');

  return (
    <div style={{ fontFamily: sans, color: colors.text }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px', marginBottom: '20px' }}>
        <div style={{ color: colors.textDim, fontSize: '13px' }}>
          {fileName} &middot; {report.params.days} hari &middot; dibuat {new Date(report.params.generatedAt).toLocaleString()}
        </div>
        <label style={{
          padding: '8px 14px', border: `1px solid ${colors.border}`, borderRadius: '6px',
          color: colors.textDim, fontSize: '13px', cursor: 'pointer',
        }}>
          Ganti file
          <input type="file" accept="application/json" onChange={onInputChange} style={{ display: 'none' }} />
        </label>
      </div>

      {error && (
        <div style={{ color: colors.short, background: colors.dangerBg, padding: '10px 14px', borderRadius: '6px', marginBottom: '20px', fontSize: '13px' }}>
          {error}
        </div>
      )}

      <div style={{ display: 'flex', gap: '16px', marginBottom: '28px', flexWrap: 'wrap' }}>
        <StatCard title="Actionable Signals" value={report.summary.actionableSignals} />
        <StatCard title="Watchlist" value={report.summary.watchlistEntries} />
        <StatCard title="Still Open" value={report.summary.stillOpen} valueColor="#7dd3fc" />
        <StatCard title="Win Rate" value={report.winRate.overall.winRate !== null ? `${report.winRate.overall.winRate}%` : 'n/a'} valueColor={colors.long} />
        <StatCard
          title="Final Cumulative R"
          value={`${report.performance.finalCumulativeR >= 0 ? '+' : ''}${report.performance.finalCumulativeR}R`}
          valueColor={report.performance.finalCumulativeR >= 0 ? colors.long : colors.short}
        />
      </div>

      <SectionTitle>Equity Curve (Cumulative R)</SectionTitle>
      <div style={{ background: colors.panel, border: `1px solid ${colors.border}`, borderRadius: '10px', padding: '16px', marginBottom: '28px' }}>
        <EquityCurveChart points={equityPoints} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '20px', marginBottom: '28px' }}>
        <div>
          <SectionTitle>Win Rate per Pattern</SectionTitle>
          <BucketTable rows={patternRows} labelHeader="Pattern" />
        </div>
        <div>
          <SectionTitle>Win Rate per Jam (UTC)</SectionTitle>
          <BucketTable rows={hourRows} labelHeader="Jam" />
        </div>
      </div>

      <SectionTitle>Performa Bulanan</SectionTitle>
      <div style={{ background: colors.panel, border: `1px solid ${colors.border}`, borderRadius: '10px', overflow: 'hidden', marginBottom: '28px' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: mono, fontSize: '13px' }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${colors.border}` }}>
                <th style={thStyle}>Bulan</th>
                <th style={thStyle}>Trades</th>
                <th style={thStyle}>Win</th>
                <th style={thStyle}>Loss</th>
                <th style={thStyle}>Net R</th>
              </tr>
            </thead>
            <tbody>
              {monthRows.length === 0 && (
                <tr><td colSpan={5} style={{ padding: '24px', textAlign: 'center', color: colors.textFaint, fontFamily: sans }}>Belum ada trade yang closed.</td></tr>
              )}
              {monthRows.map(([month, s]) => (
                <tr key={month} style={{ borderBottom: `1px solid ${colors.border}` }}>
                  <td style={tdStyle}>{month}</td>
                  <td style={tdStyle}>{s.trades}</td>
                  <td style={{ ...tdStyle, color: colors.long }}>{s.wins}</td>
                  <td style={{ ...tdStyle, color: colors.short }}>{s.losses}</td>
                  <td style={{ ...tdStyle, color: s.netR >= 0 ? colors.long : colors.short }}>
                    {s.netR >= 0 ? '+' : ''}{s.netR}R
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <SectionTitle>Signals ({actionableSignals.length})</SectionTitle>
      <div style={{ background: colors.panel, border: `1px solid ${colors.border}`, borderRadius: '10px', overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: mono, fontSize: '13px' }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${colors.border}` }}>
                <th style={thStyle}>Time</th>
                <th style={thStyle}>TF</th>
                <th style={thStyle}>Type</th>
                <th style={thStyle}>Score</th>
                <th style={thStyle}>Patterns</th>
                <th style={thStyle}>Entry</th>
                <th style={thStyle}>SL</th>
                <th style={thStyle}>TP1</th>
                <th style={thStyle}>TP2</th>
                <th style={thStyle}>Status</th>
                <th style={thStyle}>Closed @</th>
              </tr>
            </thead>
            <tbody>
              {actionableSignals.length === 0 && (
                <tr><td colSpan={11} style={{ padding: '24px', textAlign: 'center', color: colors.textFaint, fontFamily: sans }}>Tidak ada signal.</td></tr>
              )}
              {actionableSignals.map((s, i) => {
                const badge = statusStyle(s.status);
                return (
                  <tr key={`${s.candleOpenTime}-${s.timeframe}-${i}`} style={{ borderBottom: `1px solid ${colors.border}` }}>
                    <td style={{ ...tdStyle, color: colors.textDim }}>{new Date(s.createdAt).toLocaleString()}</td>
                    <td style={tdStyle}>{s.timeframe}</td>
                    <td style={{ ...tdStyle, color: s.type === 'LONG' ? colors.long : colors.short, fontWeight: 600 }}>{s.type}</td>
                    <td style={tdStyle}>{s.score}</td>
                    <td style={{ ...tdStyle, fontSize: '11px', color: colors.textDim, fontFamily: sans }}>{s.patterns.join(', ')}</td>
                    <td style={tdStyle}>{s.entryZoneStart.toFixed(0)}-{s.entryZoneEnd.toFixed(0)}</td>
                    <td style={tdStyle}>{s.stopLoss.toFixed(0)}</td>
                    <td style={tdStyle}>{s.tp1.toFixed(0)}</td>
                    <td style={tdStyle}>{s.tp2.toFixed(0)}</td>
                    <td style={tdStyle}>
                      <span style={{
                        background: badge.bg, color: badge.color, padding: '2px 8px',
                        borderRadius: '999px', fontSize: '11px', fontFamily: sans, fontWeight: 600,
                      }}>
                        {s.status}
                      </span>
                    </td>
                    <td style={{ ...tdStyle, color: colors.textDim }}>{s.closedAt ? new Date(s.closedAt).toLocaleString() : '-'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
